import React, { FC, useState } from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  TextField,
  Typography,
  Paper,
  styled,
  InputAdornment,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import { FhirResourceTypes } from '@oh-vcp/components-common';

interface EconsultClinicalReportSelectorProps {
  selectedItems: string[];
  onSelectionChanged: (selectedItems: string[]) => void;
  onSelectionDeleted?: (selectedItem: string) => void;
}

type Option = { code: string; value: string };

const options: Option[] = [
  { code: FhirResourceTypes.AllergyIntolerance, value: 'Allergy Intolerance' },
  { code: FhirResourceTypes.Condition, value: 'Clinical Conditions' },
  { code: FhirResourceTypes.FamilyMemberHistory, value: 'Family Member History' },
  { code: FhirResourceTypes.MedicationRequest, value: 'Medications' },
  { code: FhirResourceTypes.Observation, value: 'Social History and Risk Factors' },
  { code: FhirResourceTypes.Immunization, value: 'Immunization' },
  { code: FhirResourceTypes.Procedure, value: 'Procedure' },
  { code: FhirResourceTypes.DiagnosticReport, value: 'Diagnostic Report' },
  { code: FhirResourceTypes.Encounter, value: 'Encounter' }
].sort((a, b) => a.value.localeCompare(b.value));

const Container = styled(Box)(() => ({
  width: '100%'
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  height: 44,
  borderRadius: 10,
  backgroundColor: '#f7f9ff',
  border: '1.5px solid #d6e0fb',
  fontSize: 14,
  fontWeight: 500,
  marginRight: theme.spacing(1),
  marginBottom: theme.spacing(1),
  '.MuiChip-deleteIcon': {
    color: '#222',
    fontSize: 20
  }
}));

const OptionCheckbox = styled(Checkbox)(({ theme }) => ({
  fontSize: 14,
  paddingRight: 12,
  paddingLeft: 12,
  paddingTop: 6,
  paddingBottom: 6,
  marginRight: 12,
  '& .MuiSvgIcon-root': {
    fontSize: 20,
    borderRadius: 6
  },
  '&.Mui-checked': {
    color: theme.palette.primary.main
  }
}));

const StyledPaper = styled(Paper)(() => ({
  '.MuiAutocomplete-listbox': {
    paddingTop: 2,
    paddingBottom: 2
  }
}));

const EconsultClinicalReportSelector: FC<EconsultClinicalReportSelectorProps> = ({
  selectedItems,
  onSelectionChanged,
  onSelectionDeleted
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false); // ← track open state ourselves

  const valueObjects: Option[] = options.filter((o) => selectedItems.includes(o.code));

  const handleChange = (_event: React.SyntheticEvent, newValue: Option[]) => {
    onSelectionChanged(newValue.map((v) => v.code));
  };

  return (
    <Container>
      <Autocomplete<Option, true, false, false>
        multiple
        options={options}
        getOptionLabel={(option) => option.value}
        isOptionEqualToValue={(option, value) => option.code === value.code}
        value={valueObjects}
        disableCloseOnSelect
        onChange={handleChange}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        PaperComponent={(props) => <StyledPaper {...props} />}
        disablePortal={false}
        forcePopupIcon={false}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const { onDelete, ...chipProps } = getTagProps({ index });
            return (
              <StyledChip
                key={option.code}
                label={option.value}
                {...chipProps}
                onDelete={(e) => {
                  e.stopPropagation();
                  onSelectionDeleted?.(option.code);
                }}
                deleteIcon={<CloseIcon />}
              />
            );
          })
        }
        renderOption={(props, option, { selected }) => (
          <li
            {...props}
            style={{ paddingLeft: 16, paddingTop: 0, paddingBottom: 0 }}
            onClick={(e) => e.stopPropagation()}>
            <OptionCheckbox
              checked={selected}
              icon={<CheckBoxOutlineBlankIcon />}
              checkedIcon={<CheckBoxIcon />}
              onChange={() => {
                const newValue = selected
                  ? valueObjects.filter((v) => v.code !== option.code)
                  : [...valueObjects, option];
                onSelectionChanged(newValue.map((v) => v.code));
              }}
            />
            <Typography sx={{ fontSize: 14 }}>{option.value}</Typography>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <InputAdornment position="end" sx={{ position: 'absolute', top: 12, right: 8 }}>
                  <IconButton
                    size="small"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setOpen((prev) => !prev);
                    }}>
                    {open ? (
                      <ExpandLessIcon sx={{ fontSize: 20, color: 'rgba(0,0,0,0.54)' }} />
                    ) : (
                      <ExpandMoreIcon sx={{ fontSize: 20, color: 'rgba(0,0,0,0.54)' }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                fontSize: 14,
                minHeight: 56,
                padding: '8px 10px',
                paddingRight: '40px',
                '& .MuiInputBase-input': { padding: 0 }
              }
            }}
          />
        )}
        sx={{
          '.MuiAutocomplete-inputRoot': {
            padding: '6px',
            flexWrap: 'wrap'
          }
        }}
      />
    </Container>
  );
};

export default EconsultClinicalReportSelector;
